import { useAuthorizedRequest } from '@/hooks/useRequest';
import type { TeacherLessonReport, TeacherLessonReportResponse } from '@/lib/TeacherLessonReport';
import { t } from '@/lib/i18n';
import {
    Box,
    Button,
    Collapsible,
    Heading,
    HStack,
    Skeleton,
    Table,
    Text,
    VStack,
} from '@chakra-ui/react';
import { useMemo, useState } from 'react';

function jsonFetcher(url: string, token: string) {
    return fetch(url, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json());
}

type GroupBy = 'day' | 'week' | 'month';

/** Get the ISO week string "YYYY-WNN" from a date string "YYYY-MM-DD". */
function toWeekKey(dateStr: string): string {
    const d = new Date(dateStr + 'T00:00:00');
    const dayNum = d.getUTCDay() || 7; // Mon=1..Sun=7
    // Set to nearest Thursday (ISO week rule)
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const year = d.getUTCFullYear();
    const firstJan = new Date(Date.UTC(year, 0, 1));
    const days = Math.floor((d.getTime() - firstJan.getTime()) / 86400000);
    const week = Math.ceil((days + 1) / 7);
    return `${year}-W${String(week).padStart(2, '0')}`;
}

/** Get the month key "YYYY-MM" from a date string "YYYY-MM-DD". */
function toMonthKey(dateStr: string): string {
    return dateStr.slice(0, 7);
}

/** Convert an ISO week key "YYYY-WNN" to the Monday start date "YYYY-MM-DD". */
function weekKeyToStartDate(key: string): string {
    const year = Number(key.slice(0, 4));
    const week = Number(key.slice(6));
    // January 4th is always in ISO week 1
    const jan4 = new Date(Date.UTC(year, 0, 4));
    const dayOfWeek = jan4.getUTCDay() || 7; // Mon=1..Sun=7
    // Monday of ISO week 1
    const week1Monday = new Date(jan4);
    week1Monday.setUTCDate(jan4.getUTCDate() - (dayOfWeek - 1));
    // Monday of target week
    const targetMonday = new Date(week1Monday);
    targetMonday.setUTCDate(week1Monday.getUTCDate() + (week - 1) * 7);
    return targetMonday.toISOString().slice(0, 10);
}

/** Format a week key for display — shows the week start date (Monday). */
function formatWeekKey(key: string): string {
    return weekKeyToStartDate(key);
}

/** Format a month key for display. */
function formatMonthKey(key: string): string {
    const d = new Date(key + '-01T00:00:00');
    return d.toLocaleDateString('uk-UA', { year: 'numeric', month: 'long' });
}

export function TeacherLessonsReport() {
    const [groupBy, setGroupBy] = useState<GroupBy>('day');
    const { data, error, isLoading, isAuthenticated } =
        useAuthorizedRequest<TeacherLessonReportResponse>('/reports/teacher-lessons', jsonFetcher);

    const reports = useMemo(() => data?.reports ?? [], [data]);

    if (!isAuthenticated) return <div>{t('label.needs_auth')}</div>;
    if (error) return <div>{t('label.error')}: {(error as Error).message}</div>;

    return (
        <VStack gap={4} align="stretch">
            <Heading size="md">{t('reports.teacher_lessons')}</Heading>
            <HStack gap={2}>
                <Button
                    size="sm"
                    variant={groupBy === 'day' ? 'solid' : 'outline'}
                    onClick={() => setGroupBy('day')}
                >
                    {t('reports.group_by_day')}
                </Button>
                <Button
                    size="sm"
                    variant={groupBy === 'week' ? 'solid' : 'outline'}
                    onClick={() => setGroupBy('week')}
                >
                    {t('reports.group_by_week')}
                </Button>
                <Button
                    size="sm"
                    variant={groupBy === 'month' ? 'solid' : 'outline'}
                    onClick={() => setGroupBy('month')}
                >
                    {t('reports.group_by_month')}
                </Button>
            </HStack>

            {isLoading ? (
                <VStack gap={4} align="stretch">
                    {[...Array(3)].map((_, i) => (
                        <Box key={i} borderWidth="1px" borderRadius="md" p={3}>
                            <Skeleton height="24px" width="60%" mb={3} />
                            <Skeleton height="16px" width="80%" mb={2} />
                            <Skeleton height="16px" width="40%" />
                        </Box>
                    ))}
                </VStack>
            ) : reports.length === 0 ? (
                <Text color="gray.500">{t('reports.no_teacher_lessons')}</Text>
            ) : (
                <VStack gap={6} align="stretch">
                    {reports.map(report => (
                        <TeacherReportCard
                            key={report.teacher.id}
                            report={report}
                            groupBy={groupBy}
                        />
                    ))}
                </VStack>
            )}
        </VStack>
    );
}

function TeacherReportCard({
    report,
    groupBy,
}: {
    report: TeacherLessonReport;
    groupBy: GroupBy;
}) {
    const [isOpen, setIsOpen] = useState(true);

    // Compute grouped data
    const grouped = useMemo(() => {
        const map = new Map<string, { lesson_count: number; total_earned: number; company_income: number; teacher_income: number }>();

        for (const day of report.daily_stats || []) {
            let key: string;
            switch (groupBy) {
                case 'week':
                    key = toWeekKey(day.date);
                    break;
                case 'month':
                    key = toMonthKey(day.date);
                    break;
                default:
                    key = day.date;
            }
            const existing = map.get(key) ?? { lesson_count: 0, total_earned: 0, company_income: 0, teacher_income: 0 };
            existing.lesson_count += day.lesson_count;
            existing.total_earned += day.total_earned;
            existing.company_income += day.company_income;
            existing.teacher_income += day.teacher_income;
            map.set(key, existing);
        }

        // Sort by date key descending (newest first)
        const sorted = Array.from(map.entries()).sort(([a], [b]) => b.localeCompare(a));

        // Format display labels
        return sorted.map(([key, val]) => ({
            label: groupBy === 'day'
                ? formatDateLabel(key)
                : groupBy === 'week'
                    ? formatWeekKey(key)
                    : formatMonthKey(key),
            lesson_count: val.lesson_count,
            total_earned: val.total_earned,
            company_income: val.company_income,
            teacher_income: val.teacher_income,
        }));
    }, [report.daily_stats, groupBy]);

    return (
        <Box borderWidth="1px" borderRadius="md" p={3}>
            <Collapsible.Root open={isOpen} onOpenChange={(e) => setIsOpen(e.open)}>
                <Collapsible.Trigger asChild>
                    <Button variant="ghost" w="full" justifyContent="space-between" fontSize="md" fontWeight="bold">
                        <HStack gap={4}>
                            <Text>{report.teacher.name}</Text>
                            <Text fontSize="sm" fontWeight="normal" color="gray.500">
                                {t('reports.teacher_summary_full', {
                                    count: report.total_count,
                                    earned: report.total_earned,
                                    company_income: report.total_company_income,
                                    teacher_income: report.total_teacher_income,
                                })}
                            </Text>
                        </HStack>
                        <Text>{isOpen ? '▾' : '▸'}</Text>
                    </Button>
                </Collapsible.Trigger>
                <Collapsible.Content>
                    <Box overflowX="auto" mt={2}>
                        <Table.Root variant="line" showColumnBorder interactive size="sm">
                            <Table.Header>
                                <Table.Row>
                                    <Table.ColumnHeader>
                                        {groupBy === 'day' ? t('label.date') : groupBy === 'week' ? t('reports.week') : t('reports.month')}
                                    </Table.ColumnHeader>
                                    <Table.ColumnHeader>{t('reports.lesson_count')}</Table.ColumnHeader>
                                    <Table.ColumnHeader>{t('reports.total_earned')} (₴)</Table.ColumnHeader>
                                    <Table.ColumnHeader>{t('reports.company_income')} (₴)</Table.ColumnHeader>
                                    <Table.ColumnHeader>{t('reports.teacher_income')} (₴)</Table.ColumnHeader>
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {grouped.length === 0 ? (
                                    [...Array(3)].map((_, i) => (
                                        <Table.Row key={i}>
                                            {[...Array(5)].map((_, j) => (
                                                <Table.Cell key={j}><Skeleton height="20px" /></Table.Cell>
                                            ))}
                                        </Table.Row>
                                    ))
                                ) : (
                                    grouped.map(row => (
                                        <Table.Row key={row.label}>
                                            <Table.Cell>{row.label}</Table.Cell>
                                            <Table.Cell>{row.lesson_count}</Table.Cell>
                                            <Table.Cell>{row.total_earned}</Table.Cell>
                                            <Table.Cell>{row.company_income}</Table.Cell>
                                            <Table.Cell>{row.teacher_income}</Table.Cell>
                                        </Table.Row>
                                    ))
                                )}
                            </Table.Body>
                        </Table.Root>
                    </Box>
                </Collapsible.Content>
            </Collapsible.Root>
        </Box>
    );
}

function formatDateLabel(dateStr: string): string {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('uk-UA', { weekday: 'short', day: 'numeric', month: 'short' });
}
